import { Colors, Lightning } from '@lightningjs/sdk';
import { SpeechType } from '@lightningjs/ui-components';
import { getImageTextureObj } from 'support/generalUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { reportButtonClick } from 'services/analytics/reportingServicePage';
import { ButtonType } from 'services/analytics/Conviva';
import TextLabel from '../TextLabel';

const DEFAULT_HEIGHT = 70;
const DEFAULT_FONT_SIZE = 28;
const DEFAULT_PADDING = 21;
const DEFAULT_PADDING_H = 12;
const DEFAULT_MARGIN = 16;
const DEFAULT_RADIUS = 7;

export interface ButtonTemplateSpec extends Lightning.Component.TemplateSpec {
  height: number;
  title: SpeechType;
  label: string;
  action: string;
  actionArgs: unknown;
  backgroundColor: { focused?: number; unfocused?: number };
  fontColor: { focused?: number; unfocused?: number };
  fontSize: number;
  fontFace: string;
  padding: number;
  paddingH: number;
  margin: number;
  minWidth: number;
  radius: number;
  disabled: boolean;
  startIcon: {
    focused?: string | Lightning.Element.PatchTemplate;
    unfocused?: string | Lightning.Element.PatchTemplate;
    clicked?: string | Lightning.Element.PatchTemplate;
  };
  endIcon: {
    focused?: string | Lightning.Element.PatchTemplate;
    unfocused?: string | Lightning.Element.PatchTemplate;
    clicked?: string | Lightning.Element.PatchTemplate;
  };
  reportClick: boolean;

  StartIcon: Lightning.textures.ImageTexture;
  Label: typeof TextLabel;
  EndIcon: Lightning.textures.ImageTexture;
}

export default class Button<
    TemplateSpec extends ButtonTemplateSpec = ButtonTemplateSpec,
  >
  extends HoverableComponent<TemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<ButtonTemplateSpec>
{
  private _title: SpeechType | null = null;
  private _label = '';
  private _actionArgs: ButtonTemplateSpec['actionArgs'];
  private _fontSize = DEFAULT_FONT_SIZE;
  private _padding = DEFAULT_PADDING;
  private _paddingH = DEFAULT_PADDING_H;

  /** Set this variable to override reporting click events */
  reportClick = true;

  /**
   * set this variable to override button height
   */
  private _height = 0;

  private _minWidth = 0;
  private _radius = DEFAULT_RADIUS;
  private _startIcon: ButtonTemplateSpec['startIcon'] = {};
  private _endIcon: ButtonTemplateSpec['endIcon'] = {};
  // Offset for non-1080p resolutions text degradation
  private _labelSnapOffset = { x: 0, y: 0 };
  private snapLabelToPixelRef = this._snapLabelToPixel.bind(this);

  protected _backgroundColor = {
    focused: Colors('highlight').get(),
    unfocused: Colors('buttonInactive').get(),
    disabled: Colors('disabledButton').get(),
  };

  protected _fontColor = {
    focused: Colors('activeText').get(),
    unfocused: Colors('text').get(),
    disabled: Colors('disabledButtonText').get(),
  };

  /** Class name, should be overwritten by extending classes */
  protected _action = '';
  protected _buttonClass = 'Button';

  protected _margin = DEFAULT_MARGIN;
  protected _imageWidth = this._fontSize;
  protected _imageHeight = this._fontSize;

  protected _StartIcon = (this as Button).getByRef('StartIcon')!;
  protected _Label = (this as Button).getByRef('Label')!;
  protected _EndIcon = (this as Button).getByRef('EndIcon')!;

  get height() {
    return this._height;
  }

  set height(height: number) {
    this._height = height;
    this.h = height;
  }

  get title() {
    return this._title ?? this.label;
  }

  set title(title: SpeechType) {
    this._title = title;
  }

  get label() {
    return this._label;
  }

  set label(value: string) {
    this._label = value;
    this._Label.patch({ text: { text: value }, shader: null });
    this._Label.on('txLoaded', () => {
      this.updateButtonWidth();
      this._Label.removeAllListeners('txLoaded');
    });
  }

  set action(value: string) {
    this._action = value;
  }

  get actionArgs() {
    return this._actionArgs;
  }

  set actionArgs(actionArgs: ButtonTemplateSpec['actionArgs']) {
    this._actionArgs = actionArgs;
  }

  set position(value: { x?: number; y?: number; mount?: number }) {
    (this as Button).patch({ x: value.x, y: value.y, mount: value.mount });
  }

  set backgroundColor(value: {
    focused?: number;
    unfocused?: number;
    disabled?: number;
  }) {
    this._backgroundColor = {
      focused: value.focused ?? this._backgroundColor.focused,
      unfocused: value.unfocused ?? this._backgroundColor.unfocused,
      disabled: value.disabled ?? this._backgroundColor.disabled,
    };

    if (this.hasFocus()) {
      (this as Button).patch({ color: this._backgroundColor.focused });
    } else {
      (this as Button).patch({ color: this._backgroundColor.unfocused });
    }
  }

  set fontColor(value: {
    focused?: number;
    unfocused?: number;
    disabled?: number;
  }) {
    this._fontColor = {
      focused: value.focused ?? this._fontColor.focused,
      unfocused: value.unfocused ?? this._fontColor.unfocused,
      disabled: value.disabled ?? this._fontColor.disabled,
    };

    if (this.hasFocus()) {
      this._Label.patch({ text: { textColor: this._fontColor.focused } });
    } else {
      this._Label.patch({ text: { textColor: this._fontColor.unfocused } });
    }
  }

  set fontSize(value: number) {
    this._fontSize = value;
    this._Label.patch({
      text: {
        fontSize: value,
      },
    });

    this.updateButtonHeight();
  }

  set fontFace(value: string) {
    this._Label.patch({
      text: {
        fontFace: value,
      },
    });
  }

  set padding(value: number) {
    this._padding = value;
    this.updateButtonHeight();
  }

  get paddingH() {
    return this._paddingH;
  }

  set paddingH(paddingH: ButtonTemplateSpec['paddingH']) {
    this._paddingH = paddingH;
    this.updateButtonWidth();
  }

  get margin() {
    return this._margin;
  }

  set margin(value: number) {
    this._margin = value;
    this.updateButtonWidth();
  }

  get minWidth() {
    return this._minWidth;
  }

  set minWidth(minWidth: ButtonTemplateSpec['minWidth']) {
    this._minWidth = minWidth;
    this.w = Math.max(this.w, minWidth);
  }

  set radius(value: number) {
    this._radius = value;
    this.updateButtonRadius();
  }

  get startIcon() {
    return this._startIcon;
  }

  set startIcon(startIcon: ButtonTemplateSpec['startIcon']) {
    this._startIcon = startIcon;

    if (this.hasFocus()) {
      if (startIcon.focused) {
        this.setStartIcon(startIcon.focused);
      }
    } else {
      if (startIcon.unfocused) {
        this.setStartIcon(startIcon.unfocused);
      }
    }
  }

  get endIcon() {
    return this._endIcon;
  }

  set endIcon(endIcon: ButtonTemplateSpec['endIcon']) {
    this._endIcon = endIcon;

    if (this.hasFocus()) {
      if (endIcon.focused) {
        this.setEndIcon(endIcon.focused);
      }
    } else {
      if (endIcon.unfocused) {
        this.setEndIcon(endIcon.unfocused);
      }
    }
  }

  set disabled(disabled: boolean) {
    // TODO: eventually have this signal to action lists (if disabling buttons becomes more common)
    let textColor = this._fontColor.disabled;
    let backgroundColor = this._backgroundColor.disabled;
    if (!disabled) {
      // we assume a disabled button cannot be focused
      textColor = this._fontColor.unfocused;
      backgroundColor = this._backgroundColor.unfocused;
    }
    this._Label.patch({ text: { textColor } });
    (this as Button).patch({ color: backgroundColor });
  }

  static override _template(): Lightning.Component.Template<ButtonTemplateSpec> {
    return {
      h: DEFAULT_HEIGHT,
      rect: true,
      color: Colors('buttonInactive').get(),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: DEFAULT_RADIUS,
      },
      flex: {
        direction: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
      StartIcon: {},
      Label: {
        type: TextLabel,
        y: 2,
        text: {
          fontSize: DEFAULT_FONT_SIZE,
          textAlign: 'center',
          textColor: Colors('text').get(),
          fontFace: getFontFaceFromStyle('bold'),
        },
        flexItem: {
          marginLeft: DEFAULT_MARGIN,
          marginRight: DEFAULT_MARGIN,
        },
      },
      EndIcon: {},
    };
  }

  override _setup() {
    super._setup();

    const precision = this.stage.getRenderPrecision();
    // Ignore 1080p as original layout should have no visual issues
    if (precision !== 1) {
      this._Label.onAfterUpdate = this.snapLabelToPixelRef;
    }
  }

  override _getFocused(): any {
    return this;
  }

  override _focus() {
    (this as Button).patch({
      color: this._backgroundColor.focused,
    });

    this._Label.patch({
      text: { textColor: this._fontColor.focused },
    });

    if (this.startIcon.focused) {
      this.setStartIcon(this.startIcon.focused);
    }
    if (this.endIcon.focused) {
      this.setEndIcon(this.endIcon.focused);
    }
  }

  override _unfocus() {
    (this as Button).patch({
      color: this._backgroundColor.unfocused,
    });

    this._Label.patch({
      text: { textColor: this._fontColor.unfocused },
    });

    if (this.startIcon.unfocused) {
      this.setStartIcon(this.startIcon.unfocused);
    }
    if (this.endIcon.unfocused) {
      this.setEndIcon(this.endIcon.unfocused);
    }
  }

  override _handleEnter() {
    if (this.reportClick) {
      reportButtonClick({
        elementType: ButtonType.BUTTON,
        name: this._buttonClass,
        text: this.label,
      });
    }

    this._action && this.signal(this._action, this.actionArgs);
  }

  private updateButtonHeight() {
    if (this.height) return;
    (this as Button).patch({ h: this._fontSize + 2 * this._padding });
  }

  protected updateButtonWidth() {
    const hasStartIcon = !!this._StartIcon.texture?._source?.lookupId;
    const hasEndIcon = !!this._EndIcon.texture?._source?.lookupId;

    const newWidth =
      this._StartIcon.renderWidth +
      this._Label.renderWidth +
      this._EndIcon.renderWidth +
      this._margin * 2 +
      this.paddingH * 2;

    this.w = Math.max(this.minWidth, newWidth);

    const labelMargins: any = {};

    if (hasStartIcon) {
      this._StartIcon.patch({
        flexItem: { marginLeft: this.margin },
      });
    } else {
      labelMargins.marginLeft = this.margin;
    }

    if (hasEndIcon) {
      this._EndIcon.patch({
        flexItem: { marginRight: this.margin },
      });
    } else {
      labelMargins.marginRight = this.margin;
    }

    this._Label.patch({
      flexItem: labelMargins,
    });
  }

  private updateButtonRadius() {
    (this as Button).patch({
      shader: { radius: this._radius },
    });
  }

  /**
   * Below 1080, the labels can sometimes be on partial pixels (eg 6.5px)
   * This can cause visual degradation, both vertically and horizontally
   * Force the text to be a whole number
   */
  private _snapLabelToPixel() {
    if (!this.attached) return;

    const precision = this.stage.getRenderPrecision();

    // Current unscaled raw screen position
    const [currentX, currentY] = this._Label.getCornerPoints();

    // Measure from the starting position for re-renders
    const baseX = currentX - this._labelSnapOffset.x;
    const baseY = currentY - this._labelSnapOffset.y;

    const snap = (value: number) =>
      Math.round(value * precision) / precision - value;
    const offset = { x: snap(baseX), y: snap(baseY) };

    const newX = this._Label.x - this._labelSnapOffset.x + offset.x;
    const newY = this._Label.y - this._labelSnapOffset.y + offset.y;

    if (this._Label.x !== newX || this._Label.y !== newY) {
      this._Label.patch({
        x: newX,
        y: newY,
      });
    }
    this._labelSnapOffset = offset;
  }

  private setIconHelper(
    ref: Lightning.Element,
    value: string | Lightning.Element.PatchTemplate,
  ) {
    if (typeof value === 'string') {
      if (value) {
        ref.patch(
          getImageTextureObj(value, this._imageWidth, this._imageHeight),
        );
      } else {
        ref.patch({ texture: undefined });
      }
    } else {
      ref.patch({ shader: undefined });
      ref.patch(value);
    }

    ref.once('txLoaded', () => {
      this.updateButtonWidth();
      ref.removeAllListeners('txUnloaded');
    });

    ref.once('txUnloaded', () => {
      this.updateButtonWidth();
      ref.removeAllListeners('txLoaded');
    });
  }

  setStartIcon(value: string | Lightning.Element.PatchTemplate) {
    this.setIconHelper(this._StartIcon, value);
  }

  setEndIcon(value: string | Lightning.Element.PatchTemplate) {
    this.setIconHelper(this._EndIcon, value);
  }
}
